import type { CSSProperties, ElementType, ReactNode } from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface TextRevealProps {
    text: string
    as?: 'p' | 'div' | 'span' | 'blockquote' | 'h2' | 'h3'
    className?: string
    delay?: number
    stagger?: number
    trigger?: 'mount' | 'view'
}

const DURATION = 1.4

/**
 * Reveals a paragraph line by line. The real line breaks are measured after
 * the fonts load, the lines rise in one after another, then the paragraph
 * goes back to plain text so it reflows naturally on resize.
 */
const TextReveal = ({
    text,
    as = 'p',
    className,
    delay = 0,
    stagger = 0.08,
    trigger = 'view'
}: TextRevealProps): ReactNode => {
    const Component = as as ElementType
    const ref = useRef<HTMLElement>(null)
    const reduced = useReducedMotion()
    const [lines, setLines] = useState<Array<string> | null>(null)
    const [active, setActive] = useState(trigger === 'mount')
    const [done, setDone] = useState(false)
    const words = useMemo(() => text.split(/\s+/).filter(Boolean), [text])

    useEffect(() => {
        setLines(null)
        setDone(false)
    }, [text])

    useEffect(() => {
        if (trigger === 'mount') return

        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            setActive(true)
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setActive(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -12% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [trigger])

    useEffect(() => {
        if (done || reduced || lines) return

        let cancelled = false

        const measure = () => {
            const element = ref.current

            if (cancelled || !element) return

            const spans = Array.from(element.querySelectorAll<HTMLElement>('[data-word]'))
            const result: Array<string> = []
            let current: Array<string> = []
            let top: number | null = null

            spans.forEach(span => {
                const word = span.textContent ?? ''

                if (top === null || Math.abs(span.offsetTop - top) < 3) {
                    if (top === null) top = span.offsetTop
                    current.push(word)
                    return
                }

                result.push(current.join(' '))
                current = [word]
                top = span.offsetTop
            })

            if (current.length) result.push(current.join(' '))

            setLines(result)
        }

        if (document.fonts?.ready) {
            document.fonts.ready.then(measure).catch(measure)
        }
        else {
            measure()
        }

        return () => {
            cancelled = true
        }
    }, [lines, done, reduced])

    useEffect(() => {
        if (done || reduced) return

        const element = ref.current

        if (!element || typeof ResizeObserver === 'undefined') return

        let width = element.offsetWidth

        const observer = new ResizeObserver(() => {
            const next = element.offsetWidth

            if (Math.abs(next - width) > 1) {
                width = next
                setLines(null)
            }
        })

        observer.observe(element)

        return () => observer.disconnect()
    }, [done, reduced])

    useEffect(() => {
        if (!active || !lines || done) return

        const total = (delay + Math.max(lines.length - 1, 0) * stagger + DURATION) * 1000
        const id = window.setTimeout(() => setDone(true), total)

        return () => window.clearTimeout(id)
    }, [active, lines, done, delay, stagger])

    if (reduced || done) {
        return (
            <Component ref={ref} className={className}>
                {text}
            </Component>
        )
    }

    if (!lines) {
        return (
            <Component ref={ref} className={className} data-split='measure'>
                {words.map((word, index) => (
                    <span key={index}>
                        <span data-word=''>{word}</span>
                        {index < words.length - 1 ? ' ' : ''}
                    </span>
                ))}
            </Component>
        )
    }

    return (
        <Component ref={ref} className={className} data-reveal={active ? 'play' : 'wait'}>
            {lines.map((line, index) => (
                <span key={index} className='line-mask'>
                    <span className='line-inner' style={{ '--d': `${delay + index * stagger}s` } as CSSProperties}>
                        {line}{' '}
                    </span>
                </span>
            ))}
        </Component>
    )
}

export default TextReveal
